/**
 * Geolocation permission helpers for session check-in on web.
 * Uses the Permissions API when available; falls back to "unknown".
 */

import { Capacitor } from '@capacitor/core'
import { permissionDeniedMessage } from '../../../services/permissions/permission-denied'

export type GeolocationPermissionState = 'granted' | 'denied' | 'prompt' | 'unsupported' | 'unknown'

/** Non-prompting read of the browser geolocation permission state. */
export async function queryGeolocationPermission(): Promise<GeolocationPermissionState> {
  if (typeof navigator === 'undefined' || !navigator.permissions?.query) {
    return 'unknown'
  }
  try {
    const result = await navigator.permissions.query({ name: 'geolocation' })
    if (result.state === 'granted' || result.state === 'denied' || result.state === 'prompt') {
      return result.state
    }
    return 'unknown'
  } catch {
    return 'unknown'
  }
}

function siteHostname(): string {
  if (typeof window !== 'undefined' && window.location.hostname) {
    return window.location.hostname
  }
  return 'this site'
}

/**
 * Instructions when location is blocked. In the native shells this is the shared
 * Settings-oriented copy (the browser site-settings steps below are meaningless
 * there); the UI pairs it with an Open Settings button.
 */
export function geolocationDeniedMessage(): string {
  if (Capacitor.isNativePlatform()) return permissionDeniedMessage('location')
  const site = siteHostname()
  return (
    `Location access is blocked for ${site}. ` +
    `In Chrome: click the lock/tune icon in the address bar → Site settings → Location → Allow. ` +
    `In Safari: Safari menu → Settings for ${site}… → Location → Allow. Then tap Retry.`
  )
}

/** True when `message` is the denied copy above (lets the UI swap in the recoverable notice). */
export function isGeolocationDeniedMessage(message: string | null | undefined): boolean {
  return Boolean(message) && message === geolocationDeniedMessage()
}

/** Instructions when the OS or browser cannot produce a fix (common on desktop Mac). */
export function geolocationUnavailableMessage(): string {
  const site = siteHostname()
  return (
    `Your browser could not determine your location. ` +
    `On Mac: open System Settings → Privacy & Security → Location Services, turn Location Services on, ` +
    `and ensure Chrome or Safari is allowed. Then allow location for ${site} when prompted and tap Retry.`
  )
}

/** Instructions when lookup exceeds the configured timeout. */
export function geolocationTimeoutMessage(): string {
  return (
    'Timed out while getting your location. Check that Location Services are enabled ' +
    '(Mac: System Settings → Privacy & Security → Location Services) and tap Retry.'
  )
}

/** Short hint shown before recording arrival when permission is ungranted. */
export function geolocationPromptHint(): string {
  return `Recording your arrival needs your location. Tap Record arrival and choose Allow when ${siteHostname()} asks for permission.`
}
